import { useEffect, useState } from "react";
import styles from "./Navbar.module.css";
import {
  CLOCK_FORMAT_CHANGED,
  getClockFormat,
  is12HourClockFormat,
} from "../../utils/clockFormatPreference";

function getIstParts(date = new Date(), hour12 = true) {
  const parts = new Intl.DateTimeFormat(hour12 ? "en-US" : "en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12,
  }).formatToParts(date);

  const get = (type) => parts.find((p) => p.type === type)?.value ?? "00";
  const dayPeriodRaw = parts.find((p) => p.type === "dayPeriod")?.value ?? "";
  const dayPeriod = dayPeriodRaw ? dayPeriodRaw.toUpperCase() : "";

  return {
    hours: get("hour"),
    minutes: get("minute"),
    seconds: get("second"),
    dayPeriod,
  };
}

function IstClock() {
  const [format, setFormat] = useState(() => getClockFormat());
  const hour12 = is12HourClockFormat(format);
  const [time, setTime] = useState(() => getIstParts(new Date(), hour12));

  useEffect(() => {
    const onFormatChange = (event) => {
      const next = event?.detail?.format ?? getClockFormat();
      setFormat(next);
    };
    window.addEventListener(CLOCK_FORMAT_CHANGED, onFormatChange);
    return () => window.removeEventListener(CLOCK_FORMAT_CHANGED, onFormatChange);
  }, []);

  useEffect(() => {
    let timeoutId;
    let intervalId;

    const sync = () => {
      setTime(getIstParts(new Date(), hour12));
    };

    sync();

    // Align to the next whole second so the clock doesn't drift.
    const now = Date.now();
    const delay = 1000 - (now % 1000);
    timeoutId = setTimeout(() => {
      sync();
      intervalId = setInterval(sync, 1000);
    }, delay);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [hour12]);

  const displayLabel = hour12
    ? `${time.hours}:${time.minutes}:${time.seconds} ${time.dayPeriod}`
    : `${time.hours}:${time.minutes}:${time.seconds}`;

  return (
    <div
      className={styles["ist-clock"]}
      title={`India Standard Time (IST) · ${hour12 ? "12-hour" : "24-hour"}`}
      aria-live="off"
      aria-label={`IST ${displayLabel}`}
    >
      <span className={styles["ist-clock-label"]}>IST</span>
      <time
        className={styles["ist-clock-time"]}
        dateTime={`${time.hours}:${time.minutes}:${time.seconds}+05:30`}
      >
        <span className={styles["ist-clock-digit"]}>{time.hours}</span>
        <span className={styles["ist-clock-colon"]} aria-hidden>
          :
        </span>
        <span className={styles["ist-clock-digit"]}>{time.minutes}</span>
        <span className={styles["ist-clock-colon"]} aria-hidden>
          :
        </span>
        <span
          key={`${time.hours}${time.minutes}${time.seconds}${time.dayPeriod}`}
          className={`${styles["ist-clock-digit"]} ${styles["ist-clock-seconds"]}`}
        >
          {time.seconds}
        </span>
        {hour12 && time.dayPeriod ? (
          <span className={styles["ist-clock-period"]}>{time.dayPeriod}</span>
        ) : null}
      </time>
    </div>
  );
}

export default IstClock;
